import { useEffect, useRef, useCallback } from 'react';
import './BulletPunchAnimation.css';

/**
 * PROFESSIONAL BULLET PUNCH ANIMATION
 * A high-speed steel-type punching move with metallic effects
 * 
 * Key Features:
 * - Rapid multi-hit animation with blur effects
 * - Steel-type themed with metallic particles and flashes
 * - Blurred afterimages to convey extreme speed
 * - Impact shockwaves with metallic coloring
 * - Professional game-quality visual effects
 */
export default function BulletPunchAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  const particlesRef = useRef([]);
  
  // Animation phase transition hook - using console logs for debugging
  const transitionTo = useCallback((phase, delay = 0) => {
    setTimeout(() => console.log(`Animation phase: ${phase}`), delay);
  }, []);
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const container = playerEl.closest('.battle-container');
    if (!container) {
      console.error('Battle container not found');
      onComplete();
      return;
    }

    // Get positions
    const p = playerEl.getBoundingClientRect();
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate positions relative to container
    const playerX = p.left - containerRect.left + p.width / 2;
    const playerY = p.top - containerRect.top + p.height / 2;
    const opponentX = o.left - containerRect.left + o.width / 2;
    const opponentY = o.top - containerRect.top + o.height / 2;

    // TIMING CONFIGURATION - Professional gaming timing
    const TIMING = {
      preparation: 150,    // Player stance and initial energy
      dashStart: 100,      // Start of dash animation
      firstImpact: 200,    // First punch impact
      secondImpact: 150,   // Second punch impact
      thirdImpact: 150,    // Third punch impact
      finalImpact: 250,    // Final powerful impact
      returnToPosition: 200, // Return to original position
      fadeOut: 150,        // Fade out effects
      total: 1200,         // Total animation duration
    };

    console.log('👊 Bullet Punch Animation Starting');
    
    // PHASE 1: PREPARATION (0-150ms)
    // Player fighting stance preparation with initial palm energy
    transitionTo('preparation');
    playerEl.style.transition = 'transform 150ms ease-in-out';
    playerEl.style.transform = 'scale(1.1) rotate(-5deg)';
    
    // Create steel energy buildup effect around player
    const energyBuildupEffect = document.createElement('div');
    energyBuildupEffect.className = 'bullet-punch-energy-buildup';
    energyBuildupEffect.style.left = `${playerX}px`;
    energyBuildupEffect.style.top = `${playerY}px`;
    container.appendChild(energyBuildupEffect);
    
    // PHASE 2: DASH START (150-250ms)
    const dashStartTimer = setTimeout(() => {
      transitionTo('dash_start');
      console.log('👊 Phase 2: Dash Start');
      
      // Remove energy buildup
      if (energyBuildupEffect.parentNode) {
        energyBuildupEffect.parentNode.removeChild(energyBuildupEffect);
      }
      
      // Create dash blur effect
      const dashEffect = document.createElement('div');
      dashEffect.className = 'bullet-punch-dash';
      container.appendChild(dashEffect);
      animRef.current = dashEffect;
      
      // Position dash effect between player and opponent
      const dashX = playerX + (opponentX - playerX) * 0.3;
      const dashY = playerY + (opponentY - playerY) * 0.3;
      dashEffect.style.left = `${dashX}px`;
      dashEffect.style.top = `${dashY}px`;
      dashEffect.style.setProperty('--angle', `${Math.atan2(opponentY - playerY, opponentX - playerX) * 180 / Math.PI}deg`);
      
      // Hide player during dash
      playerEl.style.opacity = '0';
      
    }, TIMING.preparation);
    
    // PHASE 3: FIRST IMPACT (250-450ms)
    const firstImpactTimer = setTimeout(() => {
      transitionTo('first_impact');
      console.log('👊 Phase 3: First Impact');
      
      // Remove dash effect
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
      }
      
      // Create first punch impact
      const firstPunch = document.createElement('div');
      firstPunch.className = 'bullet-punch-impact first';
      firstPunch.style.left = `${opponentX - 10}px`;
      firstPunch.style.top = `${opponentY - 15}px`;
      container.appendChild(firstPunch);
      animRef.current = firstPunch;
      
      // Create impact flash
      const flash = document.createElement('div');
      flash.className = 'bullet-punch-flash';
      flash.style.left = `${opponentX}px`;
      flash.style.top = `${opponentY}px`;
      container.appendChild(flash);
      
      // Create metallic particles
      createMetallicParticles(container, opponentX, opponentY, 5);
      
      // Opponent reaction
      opponentEl.classList.add('bullet-punch-hit');
      
      // Clean up flash
      setTimeout(() => {
        if (flash.parentNode) {
          flash.parentNode.removeChild(flash);
        }
      }, 150);
      
    }, TIMING.preparation + TIMING.dashStart);
    
    // PHASE 4: SECOND IMPACT (450-600ms)
    const secondImpactTimer = setTimeout(() => {
      transitionTo('second_impact');
      console.log('👊 Phase 4: Second Impact');
      
      // Remove first impact
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
      }
      
      // Create second punch impact (different position)
      const secondPunch = document.createElement('div');
      secondPunch.className = 'bullet-punch-impact second';
      secondPunch.style.left = `${opponentX + 15}px`;
      secondPunch.style.top = `${opponentY + 10}px`;
      container.appendChild(secondPunch);
      animRef.current = secondPunch;
      
      // Create impact flash
      const flash = document.createElement('div');
      flash.className = 'bullet-punch-flash';
      flash.style.left = `${opponentX + 5}px`;
      flash.style.top = `${opponentY + 5}px`;
      container.appendChild(flash);
      
      // Create metallic particles
      createMetallicParticles(container, opponentX + 5, opponentY + 5, 5);
      
      // Clean up flash
      setTimeout(() => {
        if (flash.parentNode) {
          flash.parentNode.removeChild(flash);
        }
      }, 150);
      
    }, TIMING.preparation + TIMING.dashStart + TIMING.firstImpact);
    
    // PHASE 5: THIRD IMPACT (600-750ms)
    const thirdImpactTimer = setTimeout(() => {
      transitionTo('third_impact');
      console.log('👊 Phase 5: Third Impact');
      
      // Remove second impact
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
      }
      
      // Create third punch impact (different position)
      const thirdPunch = document.createElement('div');
      thirdPunch.className = 'bullet-punch-impact third';
      thirdPunch.style.left = `${opponentX - 5}px`;
      thirdPunch.style.top = `${opponentY - 5}px`;
      container.appendChild(thirdPunch);
      animRef.current = thirdPunch;
      
      // Create impact flash
      const flash = document.createElement('div');
      flash.className = 'bullet-punch-flash';
      flash.style.left = `${opponentX - 2}px`;
      flash.style.top = `${opponentY - 2}px`;
      container.appendChild(flash);
      
      // Create metallic particles
      createMetallicParticles(container, opponentX - 2, opponentY - 2, 5);
      
      // Clean up flash
      setTimeout(() => {
        if (flash.parentNode) {
          flash.parentNode.removeChild(flash);
        }
      }, 150);
      
    }, TIMING.preparation + TIMING.dashStart + TIMING.firstImpact + TIMING.secondImpact);
    
    // PHASE 6: FINAL IMPACT (750-1000ms)
    const finalImpactTimer = setTimeout(() => {
      transitionTo('final_impact');
      console.log('👊 Phase 6: Final Impact');
      
      // Remove third impact
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
      }
      
      // Create final powerful punch impact
      const finalPunch = document.createElement('div');
      finalPunch.className = 'bullet-punch-impact final';
      finalPunch.style.left = `${opponentX}px`;
      finalPunch.style.top = `${opponentY}px`;
      container.appendChild(finalPunch);
      animRef.current = finalPunch;
      
      // Create large impact shockwave
      const shockwave = document.createElement('div');
      shockwave.className = 'bullet-punch-shockwave';
      shockwave.style.left = `${opponentX}px`;
      shockwave.style.top = `${opponentY}px`;
      container.appendChild(shockwave);
      
      // Create metallic particles (more for final impact)
      createMetallicParticles(container, opponentX, opponentY, 12);
      
      // Enhanced opponent reaction
      opponentEl.classList.add('bullet-punch-hit-final');
      
      // Clean up shockwave
      setTimeout(() => {
        if (shockwave.parentNode) {
          shockwave.parentNode.removeChild(shockwave);
        }
        opponentEl.classList.remove('bullet-punch-hit');
        opponentEl.classList.remove('bullet-punch-hit-final');
      }, 200);
      
    }, TIMING.preparation + TIMING.dashStart + TIMING.firstImpact + TIMING.secondImpact + TIMING.thirdImpact);
    
    // PHASE 7: RETURN TO POSITION (1000-1200ms)
    const returnTimer = setTimeout(() => {
      transitionTo('return');
      console.log('👊 Phase 7: Return to Position');
      
      // Remove final impact
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
      }
      
      // Show player returning to position
      playerEl.style.opacity = '1';
      playerEl.style.transition = 'transform 200ms ease-out, opacity 150ms ease-in';
      playerEl.style.transform = '';
      
    }, TIMING.preparation + TIMING.dashStart + TIMING.firstImpact + TIMING.secondImpact + TIMING.thirdImpact + TIMING.finalImpact);
    
    // PHASE 8: COMPLETE (1200ms)
    const completeTimer = setTimeout(() => {
      transitionTo('complete');
      console.log('👊 Phase 8: Animation Complete');
      
      // Clean up any remaining elements
      cleanupParticles();
      
      // Animation complete callback
      onComplete();
      
    }, TIMING.total);
    
    // Helper function to create metallic particles
    function createMetallicParticles(container, x, y, count) {
      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.className = 'bullet-punch-particle';
        
        // Random position around impact point
        const angle = Math.random() * Math.PI * 2;
        const distance = 5 + Math.random() * 20;
        particle.style.left = `${x + Math.cos(angle) * distance}px`;
        particle.style.top = `${y + Math.sin(angle) * distance}px`;
        
        // Random properties for varied effect
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        particle.style.setProperty('--size', `${3 + Math.random() * 5}px`);
        particle.style.setProperty('--duration', `${0.3 + Math.random() * 0.4}s`);
        
        container.appendChild(particle);
        particlesRef.current.push(particle);
        
        // Auto-remove particles
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
            particlesRef.current = particlesRef.current.filter(p => p !== particle);
          }
        }, 400 + Math.random() * 300);
      }
    }
    
    // Helper function to clean up particles
    function cleanupParticles() {
      particlesRef.current.forEach(particle => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      });
      particlesRef.current = [];
    }

    // Enhanced cleanup function
    return () => {
      clearTimeout(dashStartTimer);
      clearTimeout(firstImpactTimer);
      clearTimeout(secondImpactTimer);
      clearTimeout(thirdImpactTimer);
      clearTimeout(finalImpactTimer);
      clearTimeout(returnTimer);
      clearTimeout(completeTimer);
      
      // Clean up energy buildup
      if (energyBuildupEffect.parentNode) {
        energyBuildupEffect.parentNode.removeChild(energyBuildupEffect);
      }
      
      // Clean up main animation element
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Clean up particles
      cleanupParticles();
      
      // Reset states
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
        playerEl.style.opacity = '1';
      }
      if (opponentEl) {
        opponentEl.classList.remove('bullet-punch-hit');
        opponentEl.classList.remove('bullet-punch-hit-final');
      }
      
      console.log('👊 Bullet Punch Animation: Cleanup completed');
    };
  }, [playerRef, opponentRef, onComplete, transitionTo]);

  return null;
}

// Set the move type for proper coloring in the battle UI
BulletPunchAnimation.moveType = "steel";
